import { useEffect, useRef, useState } from 'react'

/**
 * A bounded client-side history of a scalar the backend only sends as an instant.
 *
 * Memory pressure and GC pauses arrive as single numbers, and a single number
 * cannot answer the only question worth asking about them — "is it climbing?".
 * So the client keeps a short tail.
 *
 * Two properties matter here:
 *
 *  - Bounded. At `size` samples every push drops the head, so an eight-hour
 *    shift holds exactly as much memory as the first minute did.
 *  - Discontinuity-aware. `resetKey` changes on every snapshot, which clears the
 *    tail: a sparkline drawn straight across a reconnect gap would imply the
 *    values in between were observed. They were not.
 */
export function useRolling(
  value: number | null,
  /** Changes once per sample. Repeated renders with the same key do not append. */
  sampleKey: string | number | null,
  /** Changes on stream replacement; clears the tail. */
  resetKey: string | number | null,
  size = 150,
): readonly number[] {
  const [series, setSeries] = useState<readonly number[]>([])
  const lastSample = useRef<string | number | null>(null)
  const lastReset = useRef<string | number | null>(null)

  useEffect(() => {
    if (resetKey !== lastReset.current) {
      lastReset.current = resetKey
      lastSample.current = null
      setSeries(value === null ? [] : [value])
      return
    }

    if (sampleKey === null || sampleKey === lastSample.current || value === null) return
    lastSample.current = sampleKey

    setSeries((previous) => {
      const next = previous.length >= size ? previous.slice(previous.length - size + 1) : [...previous]
      next.push(value)
      return next
    })
  }, [value, sampleKey, resetKey, size])

  return series
}
